import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { OrgSwitcher } from "@/components/org-switcher";
import { Plus, Layers } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/segments")({
  component: SegmentsPage,
});

function SegmentsPage() {
  const { orgId } = useCurrentOrg();
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", county: "", language: "" });

  async function load() {
    if (!orgId) return;
    const { data } = await supabase.from("segments").select("*").eq("organization_id", orgId).order("created_at", { ascending: false });
    setRows(data ?? []);
  }
  useEffect(() => { load(); }, [orgId]);

  async function save() {
    if (!orgId) return;
    const filters: Record<string, string> = {};
    if (form.county) filters.county = form.county;
    if (form.language) filters.language = form.language;
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("segments").insert({
      organization_id: orgId,
      name: form.name,
      description: form.description,
      filters,
      created_by: u.user?.id,
    });
    if (error) return toast.error(error.message);
    toast.success("Segment saved");
    setOpen(false); setForm({ name: "", description: "", county: "", language: "" });
    load();
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Segments</h1>
          <p className="text-muted-foreground text-sm mt-1">Saved audience filters.</p>
        </div>
        <div className="flex gap-2">
          <OrgSwitcher />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New segment</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create segment</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>County</Label><Input value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} /></div>
                  <div><Label>Language</Label><Input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} /></div>
                </div>
                <Button onClick={save} className="w-full" disabled={!form.name}>Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((s) => (
          <Card key={s.id}>
            <CardHeader className="flex flex-row items-center gap-3">
              <Layers className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>{s.name}</CardTitle>
                <div className="text-xs text-muted-foreground">{s.description || "—"}</div>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-2 rounded">{JSON.stringify(s.filters, null, 2)}</pre>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && <Card className="md:col-span-2"><CardContent className="p-12 text-center text-muted-foreground">No segments yet</CardContent></Card>}
      </div>
    </div>
  );
}
